/* © NXAPP Limited */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { Plus, Megaphone, Trash2, X, ChevronRight } from 'lucide-react'
import EmptyState from '@/components/ui/EmptyState'
import Spinner from '@/components/ui/Spinner'
import { formatDate } from '@/lib/utils'
import TrendStrip from '@/components/dashboard/TrendStrip'
import RowSplit from '@/components/dashboard/RowSplit'
import { dayKeys, bucketCount, addSeries, emptySplit, addToSplit, londonDayKey, type Split } from '@/lib/trend'

export interface CampaignRow {
  id: string
  user_id: string
  name: string
  description: string | null
  colour: string
  icon: string
  audience: 'director' | 'company'
  sending_profile_id: string | null
  batch_size: number | null
  daily_cap: number | null
  throttle_ms: number | null
  kind: 'broadcast' | 'sequence' | 'individual' | 'weekly' | 'tm'
  window_start_hour: number
  window_end_hour: number
  window_weekdays_only: boolean
  default_audience: { campaignLeads?: boolean; allSubscribed?: boolean; segmentIds?: string[] } | null
  auto_send: boolean
  public_subscribe?: boolean
  public_title?: string | null
  public_description?: string | null
  created_at: string
}

export interface ProfileOption {
  id: string; name: string; from_email: string
  brand_id?: string | null
  brandName?: string | null
}

const PRESET_COLOURS = [
  '#6366f1', '#0ea5e9', '#10b981', '#f59e0b',
  '#ef4444', '#8b5cf6', '#ec4899', '#64748b',
]

const PRESET_ICONS = ['📰','📧','🚀','📊','💼','🎯','💡','🔔','🌐','⚡']

function CampaignModal({
  initial,
  profiles,
  onSave,
  onClose,
  saving,
}: {
  initial?: Partial<CampaignRow>
  profiles: ProfileOption[]
  onSave: (v: { name: string; description: string; colour: string; icon: string; audience: 'director' | 'company'; kind: 'broadcast' | 'sequence' | 'individual' | 'weekly' | 'tm'; sending_profile_id: string | null }) => void
  onClose: () => void
  saving: boolean
}) {
  const [name, setName]        = useState(initial?.name ?? '')
  const [description, setDesc] = useState(initial?.description ?? '')
  const [colour, setColour]    = useState(initial?.colour ?? '#6366f1')
  const [icon, setIcon]        = useState(initial?.icon ?? '📧')
  const [audience, setAudience] = useState<'director' | 'company'>(initial?.audience ?? 'director')
  const [kind, setKind] = useState<'broadcast' | 'sequence' | 'individual' | 'weekly' | 'tm'>(initial?.kind ?? 'broadcast')
  const [profileId, setProfileId] = useState(initial?.sending_profile_id ?? '')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">{initial?.id ? 'Edit campaign' : 'New campaign'}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Icon picker */}
          <div>
            <label className="label">Icon</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {PRESET_ICONS.map(e => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setIcon(e)}
                  className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-all ${
                    icon === e ? 'ring-2 ring-offset-1 ring-brand-600 scale-110' : 'hover:bg-gray-100'
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Colour picker */}
          <div>
            <label className="label">Colour</label>
            <div className="flex gap-2 mt-1">
              {PRESET_COLOURS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColour(c)}
                  style={{ backgroundColor: c }}
                  className={`w-7 h-7 rounded-full transition-all ${
                    colour === c ? 'ring-2 ring-offset-2 ring-gray-600 scale-110' : 'hover:scale-105'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Audience type */}
          <div>
            <label className="label">Campaign type</label>
            <div className="grid grid-cols-2 gap-2 mt-1 mb-4">
              {([
                ['broadcast', '📢 Broadcast', 'Issues you write and schedule — weekly digest, newsletters'],
                ['weekly', '🗓️ Weekly Email (AI)', 'Paste a brief; AI drafts one each week from it; approve or auto-send'],
                ['sequence', '🔁 Sequence', 'Timed follow-ups from each person’s own join date'],
                ['individual', '🎯 Individual Outreach', 'Import a list; AI personalises each email; you approve before every send'],
                ['tm', '📞 TM (Telemarketing)', 'A call queue. Feed it from Individual Outreach with “Copy Data to TM”'],
              ] as const).map(([v, lbl, hint]) => (
                <button key={v} type="button" onClick={() => setKind(v)}
                  className={`flex-1 text-left rounded-lg border px-3 py-2 transition-all ${kind === v ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <p className="text-xs font-semibold text-gray-800">{lbl}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{hint}</p>
                </button>
              ))}
            </div>

            <label className="label">Audience</label>
            <div className="flex gap-2 mt-1">
              {([
                ['director', '👤 Directors', 'Personal emails — warm 1:1 outreach'],
                ['company', '🏢 Company inboxes', 'Generic info@ — cold company outreach'],
              ] as const).map(([v, lbl, hint]) => (
                <button key={v} type="button" onClick={() => setAudience(v)}
                  className={`flex-1 text-left rounded-lg border px-3 py-2 transition-all ${audience === v ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <p className="text-xs font-semibold text-gray-800">{lbl}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{hint}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Campaign name *</label>
            <input
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Weekly Tech Digest, New Companies…"
              autoFocus
            />
          </div>

          <div>
            <label className="label">Description</label>
            <input
              className="input"
              value={description}
              onChange={e => setDesc(e.target.value)}
              placeholder="Short description of this campaign"
            />
          </div>

          {/* Sending profile — which identity this campaign sends from */}
          <div>
            <label className="label">Sending profile</label>
            <select className="input" value={profileId} onChange={e => setProfileId(e.target.value)}>
              <option value="">— none yet (set later) —</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.name} ({p.from_email})</option>)}
            </select>
            <p className="mt-1 text-xs text-gray-400">Issues in this campaign send from this profile unless an issue overrides it. Manage profiles in Settings.</p>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button
            disabled={!name.trim() || saving}
            onClick={() => onSave({ name: name.trim(), description: description.trim(), colour, icon, audience, kind, sending_profile_id: profileId || null })}
            className="btn-primary flex-1 justify-center disabled:opacity-40"
          >
            {saving ? 'Saving…' : initial?.id ? 'Save changes' : 'Create campaign'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function CampaignsPage() {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['campaigns', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('campaigns')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: true })
      return (data ?? []) as CampaignRow[]
    },
    enabled: !!user,
  })

  // Issue counts per campaign
  const { data: issueCounts } = useQuery({
    queryKey: ['campaign-issue-counts', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('issues')
        .select('campaign_id')
        .eq('user_id', user!.id)
      const counts: Record<string, number> = {}
      for (const row of data ?? []) {
        if (row.campaign_id) counts[row.campaign_id] = (counts[row.campaign_id] ?? 0) + 1
      }
      return counts
    },
    enabled: !!user,
  })

  // Module dashboard — emails sent / opens / clicks over the last 30 days, both
  // aggregated (top strip) and split per campaign (list rows). Issue sends are
  // attributed to a campaign via issues.campaign_id; outreach sends carry it
  // directly; opens/clicks map event → send → issue → campaign.
  const { data: trend, isLoading: trendLoading } = useQuery({
    queryKey: ['campaigns-trend', user?.id],
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86_400_000).toISOString()
      const [issuesR, sendsR, outreachR, eventsR, assignsR] = await Promise.all([
        supabase.from('issues').select('id, campaign_id').eq('user_id', user!.id).limit(10000),
        supabase.from('sends').select('id, issue_id, sent_at').eq('user_id', user!.id).not('sent_at', 'is', null).gte('sent_at', since).limit(10000),
        supabase.from('outreach_drafts').select('campaign_id, sent_at').eq('user_id', user!.id).eq('status', 'sent').gte('sent_at', since).limit(10000),
        supabase.from('events').select('send_id, type, created_at').in('type', ['OPEN', 'CLICK']).gte('created_at', since).limit(10000),
        supabase.from('lead_campaign_assignments').select('campaign_id, created_at').eq('user_id', user!.id).gte('created_at', since).limit(10000),
      ])
      const keys = dayKeys(30)
      const nowMs = Date.now(); const todayKey = londonDayKey(new Date())
      const issues = (issuesR.data ?? []) as { id: string; campaign_id: string | null }[]
      const sends = (sendsR.data ?? []) as { id: string; issue_id: string; sent_at: string }[]
      const outreach = (outreachR.data ?? []) as { campaign_id: string; sent_at: string }[]
      const events = (eventsR.data ?? []) as { send_id: string; type: string; created_at: string }[]
      const assigns = (assignsR.data ?? []) as { campaign_id: string; created_at: string }[]

      const issueToCampaign = new Map(issues.map(i => [i.id, i.campaign_id]))
      const sendToCampaign = new Map(sends.map(s => [s.id, issueToCampaign.get(s.issue_id) ?? null]))

      // Per-campaign split into Today / This week / This month.
      const byCampaign: Record<string, { added: Split; sent: Split; opens: Split; clicks: Split }> = {}
      const bump = (cid: string | null | undefined, k: 'added' | 'sent' | 'opens' | 'clicks', ts: string) => {
        if (!cid) return
        const row = (byCampaign[cid] ??= { added: emptySplit(), sent: emptySplit(), opens: emptySplit(), clicks: emptySplit() })
        addToSplit(row[k], ts, 1, nowMs, todayKey)
      }
      for (const a of assigns) bump(a.campaign_id, 'added', a.created_at)
      for (const s of sends) bump(issueToCampaign.get(s.issue_id), 'sent', s.sent_at)
      for (const o of outreach) bump(o.campaign_id, 'sent', o.sent_at)
      for (const e of events) bump(sendToCampaign.get(e.send_id), e.type === 'OPEN' ? 'opens' : 'clicks', e.created_at)

      // Aggregate daily series for the top strip (opens/clicks come from opensTrend).
      const addedSeries = bucketCount(assigns, keys, r => r.created_at)
      const sentSeries = addSeries(
        bucketCount(sends, keys, r => r.sent_at),
        bucketCount(outreach, keys, r => r.sent_at),
      )
      return { byCampaign, sentSeries, addedSeries }
    },
  })

  // Aggregate opens/clicks trend (own small fetch so the sparkline is real).
  const { data: opensTrend } = useQuery({
    queryKey: ['campaigns-opens-trend', user?.id],
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86_400_000).toISOString()
      const { data } = await supabase.from('events').select('type, created_at').in('type', ['OPEN', 'CLICK']).gte('created_at', since).limit(10000)
      const keys = dayKeys(30)
      const ev = (data ?? []) as { type: string; created_at: string }[]
      return {
        opens: bucketCount(ev.filter(e => e.type === 'OPEN'), keys, r => r.created_at),
        clicks: bucketCount(ev.filter(e => e.type === 'CLICK'), keys, r => r.created_at),
      }
    },
  })

  const strip = (trend && opensTrend) ? [
    { label: 'Added to list', tone: 'green' as const, series: trend.addedSeries },
    { label: 'Emails sent', tone: 'indigo' as const, series: trend.sentSeries },
    { label: 'Opens', tone: 'teal' as const, series: opensTrend.opens },
    { label: 'Clicks', tone: 'amber' as const, series: opensTrend.clicks },
  ] : undefined

  const { data: profileOptions } = useQuery({
    queryKey: ['sending-profiles-options', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('sending_profiles')
        .select('id, name, from_email, brand_id, brands(name)')
        .eq('user_id', user!.id).order('name')
      // Supabase returns a single object for this FK, but guard both shapes.
      return (data ?? []).map(p => {
        const b = Array.isArray(p.brands) ? p.brands[0] : p.brands
        return { ...p, brandName: (b as { name: string } | null)?.name ?? null }
      }) as ProfileOption[]
    },
  })

  const create = useMutation({
    mutationFn: async (v: { name: string; description: string; colour: string; icon: string; audience: 'director' | 'company'; kind: 'broadcast' | 'sequence' | 'individual' | 'weekly' | 'tm'; sending_profile_id: string | null }) => {
      const { error } = await supabase.from('campaigns').insert({ ...v, user_id: user!.id })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] })
      setShowCreate(false)
    },
  })

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('campaigns').delete().eq('id', id).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">Each campaign is an independent mailing programme with its own issues and templates</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <Plus className="w-4 h-4" /> New campaign
        </button>
      </div>

      <TrendStrip metrics={strip} loading={trendLoading} />

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : !campaigns?.length ? (
        <EmptyState
          icon={Megaphone}
          title="No campaigns yet"
          description="Create your first campaign to start sending newsletters."
          action={<button onClick={() => setShowCreate(true)} className="btn-primary">New campaign</button>}
        />
      ) : (
        <div className="card overflow-hidden divide-y divide-gray-100">
          {/* Column headings — the list is dense enough to need them */}
          <div className="hidden md:flex items-center gap-4 px-4 py-2 bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            <span className="w-9 shrink-0" />
            <span className="flex-1">Campaign</span>
            <span className="w-40 shrink-0">Sends from</span>
            <span className="w-14 text-right shrink-0">Issues</span>
            <span className="hidden xl:block w-24 text-right shrink-0">Created</span>
            <span className="w-[52px] shrink-0" />
          </div>
          {campaigns.map(c => {
            const prof = (profileOptions ?? []).find(p => p.id === c.sending_profile_id)
            return (
            <Link
              key={c.id}
              to={`/campaigns/${c.id}`}
              className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors group"
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center text-lg shrink-0 select-none"
                style={{ backgroundColor: c.colour + '20' }}
              >
                {c.icon}
              </div>

              {/* Name gets the whole flexible column; badges sit underneath so a
                  long campaign name is never truncated to make room for them. */}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{c.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${c.audience === 'company' ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-brand-50 text-brand-600 border-brand-200'}`}>
                    {c.audience === 'company' ? '🏢 Company' : '👤 Directors'}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${c.kind === 'sequence' ? 'bg-amber-50 text-amber-700 border-amber-200' : c.kind === 'individual' ? 'bg-teal-50 text-teal-700 border-teal-200' : c.kind === 'weekly' ? 'bg-brand-50 text-brand-700 border-brand-200' : c.kind === 'tm' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                    {c.kind === 'sequence' ? '🔁 Sequence' : c.kind === 'individual' ? '🎯 Individual' : c.kind === 'weekly' ? '🗓️ Weekly (AI)' : c.kind === 'tm' ? '📞 TM' : '📢 Broadcast'}
                  </span>
                  {c.description && (
                    <span className="text-xs text-gray-400 truncate">{c.description}</span>
                  )}
                </div>
                {/* Per-campaign split: Today / This week / This month */}
                {(() => {
                  const st = trend?.byCampaign?.[c.id]
                  return (
                    <span className="block mt-1.5 max-w-xs">
                      <RowSplit metrics={[
                        { label: 'Added', tone: 'green', split: st?.added ?? emptySplit() },
                        { label: 'Sent', tone: 'indigo', split: st?.sent ?? emptySplit() },
                        { label: 'Opens', tone: 'teal', split: st?.opens ?? emptySplit() },
                        { label: 'Clicks', tone: 'amber', split: st?.clicks ?? emptySplit() },
                      ]} />
                    </span>
                  )
                })()}
              </div>

              {/* Sending profile + its brand — who this campaign goes out as */}
              <div className="hidden md:block w-40 shrink-0 min-w-0">
                {prof ? (
                  <>
                    <p className="text-xs text-gray-600 truncate">{prof.from_email}</p>
                    {prof.brandName
                      ? <p className="text-[10px] text-indigo-600 truncate">{prof.brandName}</p>
                      : <p className="text-[10px] text-amber-600">No brand</p>}
                  </>
                ) : (
                  <p className="text-xs text-amber-600">⚠ No profile</p>
                )}
              </div>

              <p className="hidden md:block text-xs text-gray-400 w-14 text-right shrink-0">
                {issueCounts?.[c.id] ?? 0}
              </p>
              <p className="hidden xl:block text-xs text-gray-400 w-24 text-right shrink-0">
                {formatDate(c.created_at)}
              </p>

              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={e => {
                    e.preventDefault()
                    if (confirm(`Delete "${c.name}"? All its issues will be unassigned.`)) del.mutate(c.id)
                  }}
                  className="p-1.5 text-gray-300 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-all"
                  title="Delete campaign"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </div>
            </Link>
            )
          })}
        </div>
      )}

      {showCreate && (
        <CampaignModal
          profiles={profileOptions ?? []}
          onSave={v => create.mutate(v)}
          onClose={() => setShowCreate(false)}
          saving={create.isPending}
        />
      )}
    </div>
  )
}
